import ExpandMore from "@mui/icons-material/ExpandMore";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { StatusChip, StatusChipColor } from "components/StatusChip";
import {
  AmbiguousFacilityMatch,
  BlockedFacility,
  BlockedFacilityReason,
  FacilityMatchSource,
  InventoryChangeDetail,
  MissingDensityCause,
  MissingDensityChemical,
  RunSummary,
  UnmatchedSite,
} from "encamp-shared/src/hmbp/wadmRunSummary";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";

const BLOCKED_REASON_LABELS: Record<BlockedFacilityReason, string> = {
  unmatchedProfileCode: "Unmapped profile code",
  ambiguousChemical: "Ambiguous chemical match",
  missingChemical: "Chemical missing from catalog",
  colocationParentNotInCalifornia: "Co-located reporting facility not in California",
  missingDensity: "Density required for unit conversion is missing",
  missingCersId: "Facility is missing a CERS ID",
};

const MISSING_DENSITY_CAUSE_LABELS: Record<MissingDensityCause, string> = {
  chemicalHasNoDensity: "Chemical has no density; pounds can't convert to its reporting unit",
  storageRowUnitConversion:
    "A customer storage row is in a different unit; summing it needs a density the chemical lacks",
};

/**
 * Inventory-change "Kind" chip. created → success, updated → info; an unchanged
 * row uses the plain grey outlined default from the chip spec (no semantic
 * color), since it carries no signal.
 */
function InventoryKindChip({ kind }: { kind: InventoryChangeDetail["kind"] }) {
  if (kind === "created") return <StatusChip label={kind} color="success" />;
  if (kind === "updated") return <StatusChip label={kind} color="info" />;
  return <StatusChip label={kind} color="default" variant="outlined" />;
}

/**
 * Renders the WADM run summary stored on `Job.output` for `PROCESS_AMAZON_WADM`
 * jobs. Two accordions:
 *   - "Run Summary" — top-line counts, CUPA breakdown, chip lists for
 *     unmatched profile codes / missing chemicals / unmatched site codes /
 *     facilities with a pre-existing open UPDATE that this run skipped.
 *   - "Inventory Changes" — per-row before/after table.
 *
 * The caller is responsible for gating render on
 * `task === ProcessAmazonWadm && isWadmRunSummary(output)` so this component
 * receives a fully-typed summary.
 */
export function WadmRunSummarySection({ summary }: { summary: RunSummary }) {
  return (
    <Stack spacing={2}>
      <Accordion defaultExpanded>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle1" fontWeight="medium">
            WADM Run Summary
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <RunSummaryBody summary={summary} />
        </AccordionDetails>
      </Accordion>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle1" fontWeight="medium">
            Inventory Changes ({summary.inventoryChanges.length})
          </Typography>
        </AccordionSummary>
        <AccordionDetails>
          <InventoryChangesTable changes={summary.inventoryChanges} />
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}

function RunSummaryBody({ summary }: { summary: RunSummary }) {
  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={4} flexWrap="wrap">
        <CounterStat
          label="Facilities matched"
          value={summary.facilitiesMatched}
          secondary={`of ${summary.facilitiesAttempted.toLocaleString()} attempted`}
        />
        <CounterStat label="Items updated" value={summary.itemsUpdated} />
        <CounterStat label="Reports created" value={summary.reportsCreated} />
      </Stack>

      <BlockedFacilitiesTable blockedFacilities={summary.blockedFacilities ?? []} />
      <MissingDensityTable chemicals={summary.missingDensityChemicals ?? []} />

      {summary.cupaBreakdown.length > 0 && (
        <Box>
          <Typography variant="overline" color="text.secondary">
            CUPA breakdown
          </Typography>
          <TableContainer sx={{ mt: 0.5 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>CUPA</TableCell>
                  <TableCell>County</TableCell>
                  <TableCell align="right">Reports</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summary.cupaBreakdown.map((c, i) => (
                  <TableRow key={`${c.lepcName ?? "null"}::${c.county ?? "null"}::${i}`}>
                    <TableCell>{c.lepcName ?? "—"}</TableCell>
                    <TableCell>{c.county ?? "—"}</TableCell>
                    <TableCell align="right">{c.reportCount}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      <ChipList
        label="Unmatched profile codes"
        color="error"
        values={summary.unmatchedProfileCodes}
      />
      <ChipList label="Missing chemicals" color="error" values={summary.missingChemicals} />
      <UnmatchedSites siteCodes={summary.unmatchedSiteCodes} sites={summary.unmatchedSites} />
      <ChipList
        label="Facilities with an open UPDATE (skipped this run)"
        color="info"
        values={openUpdateLabels(summary)}
      />
    </Stack>
  );
}

/**
 * Display labels for the open-UPDATE list: facility names when the summary
 * carries them, ids for historical summaries (or a facility that no longer
 * resolves to a name).
 */
function openUpdateLabels(summary: RunSummary): string[] {
  const nameById = new Map(
    (summary.facilitiesWithExistingOpenUpdate ?? []).map((f) => [f.facilityId, f.facilityName])
  );
  return summary.facilityIdsWithExistingOpenUpdate.map((id) => nameById.get(id) ?? id);
}

const MATCH_SOURCE_LABELS: Record<FacilityMatchSource, string> = {
  epaId: "EPA ID",
  name: "name",
};

function describeAmbiguousMatches(matches: AmbiguousFacilityMatch[]): string {
  const candidates = matches.map((m) => `${m.facilityName} (${m.facilityId}, ${m.state})`);
  const sources = [...new Set(matches.map((m) => MATCH_SOURCE_LABELS[m.matchedBy]))].join(" / ");
  return `Ambiguous: ${matches.length} eligible facilities match by ${sources}: ${candidates.join(", ")}`;
}

/** The "Note" cell: why this site code did not resolve to a facility. */
function unmatchedSiteNote(u: UnmatchedSite): string {
  if (u.ambiguousMatches && u.ambiguousMatches.length > 0) {
    return describeAmbiguousMatches(u.ambiguousMatches);
  }
  if (u.excludedMatch) {
    return `Only match is ${prettyPrintEnumValue(u.excludedMatch.status)} facility ${u.excludedMatch.facilityName}`;
  }
  return "No facility in this tenant matches the site code or EPA ID";
}

/**
 * Unmatched site codes. The structured list (EPA id + status-excluded hit,
 * or the candidates of an ambiguous match) renders as a table; a historical
 * summary without it falls back to chips.
 */
function UnmatchedSites({
  siteCodes,
  sites,
}: {
  siteCodes: string[];
  sites: UnmatchedSite[] | undefined;
}) {
  if (sites === undefined) {
    return <ChipList label="Unmatched site codes" color="error" values={siteCodes} />;
  }
  if (sites.length === 0) return null;
  return (
    <Box>
      <Typography variant="overline" color="error">
        Unmatched site codes ({sites.length})
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Site Code</TableCell>
              <TableCell>EPA ID</TableCell>
              <TableCell>Note</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sites.map((u) => (
              <TableRow key={u.siteCode}>
                <TableCell>{u.siteCode}</TableCell>
                <TableCell>{u.regulatoryId ?? "—"}</TableCell>
                <TableCell>{unmatchedSiteNote(u)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function MissingDensityTable({ chemicals }: { chemicals: MissingDensityChemical[] }) {
  if (chemicals.length === 0) return null;
  return (
    <Box>
      <Typography variant="overline" color="error">
        Chemicals missing a density ({chemicals.length})
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        Each row held its facility back. Give the chemical a density, or put the storage row in the
        chemical's reporting unit, and re-run the cycle.
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Facility</TableCell>
              <TableCell>Chemical</TableCell>
              <TableCell>Chemical ID</TableCell>
              <TableCell>Cause</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {chemicals.map((m) => (
              <TableRow key={`${m.facilityId}::${m.chemicalId}`}>
                <TableCell>{m.facilityName ?? m.facilityId}</TableCell>
                <TableCell>{m.chemicalName ?? m.chemicalId}</TableCell>
                <TableCell>{m.chemicalId}</TableCell>
                <TableCell>{MISSING_DENSITY_CAUSE_LABELS[m.cause]}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function CounterStat({
  label,
  value,
  secondary,
}: {
  label: string;
  value: number;
  secondary?: string;
}) {
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="h5" fontWeight="medium">
        {value.toLocaleString()}
      </Typography>
      {secondary !== undefined && (
        <Typography variant="caption" color="text.secondary">
          {secondary}
        </Typography>
      )}
    </Box>
  );
}

function ChipList({
  label,
  color,
  values,
}: {
  label: string;
  color: StatusChipColor;
  values: string[];
}) {
  if (values.length === 0) return null;
  return (
    <Box>
      <Typography variant="overline" color="text.secondary">
        {label} ({values.length})
      </Typography>
      <Box display="flex" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
        {values.map((v) => (
          <StatusChip key={v} label={v} color={color} />
        ))}
      </Box>
    </Box>
  );
}

function BlockedFacilitiesTable({ blockedFacilities }: { blockedFacilities: BlockedFacility[] }) {
  if (blockedFacilities.length === 0) return null;
  return (
    <Box>
      <Typography variant="overline" color="error">
        Skipped facilities ({blockedFacilities.length})
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        These facilities were not written this run because some of their rows could not be mapped.
        Fix the mapping(s) below and re-run the cycle.
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Site Code</TableCell>
              <TableCell>Facility</TableCell>
              <TableCell>Reasons</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {blockedFacilities.map((b) => (
              <TableRow key={b.siteCode}>
                <TableCell>{b.siteCode}</TableCell>
                <TableCell>{b.facilityName ?? b.facilityId ?? "—"}</TableCell>
                <TableCell>
                  <Box display="flex" flexWrap="wrap" gap={0.5}>
                    {b.reasons.map((r) => (
                      <StatusChip key={r} color="error" label={BLOCKED_REASON_LABELS[r]} />
                    ))}
                  </Box>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}

function InventoryChangesTable({ changes }: { changes: InventoryChangeDetail[] }) {
  if (changes.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No inventory rows were created or updated this run.
      </Typography>
    );
  }
  return (
    // Cap the height so very large runs (thousands of rows) scroll within a
    // bounded region instead of expanding the page inline. Header stays
    // sticky as the user scrolls.
    <TableContainer sx={{ maxHeight: 480 }}>
      <Table size="small" stickyHeader>
        <TableHead>
          <TableRow>
            <TableCell>Facility</TableCell>
            <TableCell>Chemical</TableCell>
            <TableCell>Kind</TableCell>
            <TableCell align="right">Prior Max</TableCell>
            <TableCell align="right">New Max</TableCell>
            <TableCell align="right">Max Code</TableCell>
            <TableCell align="right">Prior Avg</TableCell>
            <TableCell align="right">New Avg</TableCell>
            <TableCell align="right">Avg Code</TableCell>
            <TableCell align="right">Annual Waste Amount</TableCell>
            <TableCell>Unit</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {changes.map((c) => (
            <TableRow key={`${c.facilityId}::${c.chemicalId}`}>
              <TableCell>{c.facilityName ?? c.facilityId}</TableCell>
              <TableCell>{c.chemicalName ?? c.chemicalId}</TableCell>
              <TableCell>
                <InventoryKindChip kind={c.kind} />
              </TableCell>
              <TableCell align="right">{formatAmount(c.priorMaxAmount)}</TableCell>
              <TableCell align="right">{formatAmount(c.newMaxAmount)}</TableCell>
              <TableCell align="right">
                {formatCodeTransition(c.priorMaxAmountCode, c.newMaxAmountCode)}
              </TableCell>
              <TableCell align="right">{formatAmount(c.priorAverageAmount)}</TableCell>
              <TableCell align="right">{formatAmount(c.newAverageAmount)}</TableCell>
              <TableCell align="right">
                {formatCodeTransition(c.priorAverageAmountCode, c.newAverageAmountCode)}
              </TableCell>
              <TableCell align="right">{formatAmount(c.annualWasteAmount)}</TableCell>
              <TableCell>{prettyPrintEnumValue(c.unit)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function formatAmount(n: number | null): string {
  if (n === null) return "—";
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatCodeTransition(prior: number | null, next: number | null): string {
  if (prior === null && next === null) return "—";
  if (prior === null) return String(next);
  if (prior === next) return String(next);
  return `${prior} → ${next ?? "—"}`;
}
